import { useState, useEffect, useCallback, useRef } from "react";
import { supabase, dbToDeal } from "../../supabase";
import { parseDealName } from "../../utils/parseDealName";
import { getWeekStartISO, getWeekEndISO, getCurrentWeekWindow } from "../../utils/weeklyWindow";
import PPLogo from "../common/PPLogo";
import { MAP_LAYER_COLORS } from "../../constants/mapLayers";

// ═══════════════════════════════════════════════════════════════════════════════
// WEEKLY MEETING PRESENTATION — LIGHT BRAND
// ═══════════════════════════════════════════════════════════════════════════════
// Slides: Title → Weekly Summary → LOI Submitted → Underwriting → Tracking → Outreach → Map

const PP_RED = "#e93529";
const TEXT = "#333333";
const MUTED = "#8a8a8a";
const BORDER = "#d8d8d8";
const BG = "#f4f4f4";
const CARD = "#ffffff";
const FONT = "'Montserrat', 'Helvetica Neue', Arial, sans-serif";

const fmtMoney = (n) => {
  if (!n) return "TBD";
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
};
const fmtSF = (n) => n ? `${Number(n).toLocaleString()} SF` : "TBD";
const fmtPSF = (n) => n ? `$${Number(n).toLocaleString()}` : "TBD";

// Snapshots store a precomputed `snapshotName` instead of the full
// sourceNotes blob (which carries appended email summaries and would bloat
// the stored payload), so prefer it when present.
const dealName = (d) =>
  d?.snapshotName || parseDealName(d?.sourceNotes) || d?.addresses?.[0] || "";

// Only the fields the slides actually render. Keeps a stored week to a
// couple hundred KB instead of dumping every column of every deal.
const trimDeal = (d) => ({
  id: d.id,
  snapshotName: dealName(d),
  addresses: d.addresses?.slice(0, 1) || [],
  stage: d.stage,
  market: d.market,
  msas: d.msas || [],
  squareFeet: d.squareFeet,
  askingPrice: d.askingPrice,
  underwrittenPrice: d.underwrittenPrice,
  capRate: d.capRate,
  marketed: d.marketed,
  latitude: d.latitude,
  longitude: d.longitude,
  createdAt: d.createdAt,
  madeBestAndFinal: d.madeBestAndFinal,
});

// Bumped when the stored shape changes. v1 snapshots (written before the
// deck owned this) hold only aggregate totals and cannot rebuild the deal
// tables, so the deck refuses to render them rather than showing blanks.
export const SNAPSHOT_VERSION = 2;

export default function WeeklyPresentation({ onClose, exportMode = false, snapshot = null, onSnapshotSaved, presentedBy = null, presentedByName = null }) {
  const [slide, setSlide] = useState(0);
  const [deals, setDeals] = useState([]);
  const [comps, setComps] = useState([]);
  const [outreachCount, setOutreachCount] = useState(0);
  // Map of deal_id → most recent LOI PSF + total (used by LOI Submitted slide
  // so the $/SF column reflects what we actually offered, not the seller's ask).
  const [loiByDeal, setLoiByDeal] = useState({});
  const [loading, setLoading] = useState(true);
  // Weekly Summary data
  const [agendaItems, setAgendaItems] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  // Discussion items (legacy — now part of Weekly Summary)
  const [discussionItems, setDiscussionItems] = useState([]);
  const [newItemText, setNewItemText] = useState("");
  const [newItemCategory, setNewItemCategory] = useState("general");
  // Map slide filters
  const [mapFilters, setMapFilters] = useState({
    "LOI Submitted": true, "Unsolicited Offer": true, "Underwriting": true,
    "Screening": true, "Tracking": true, "Sourcing": true, "Outreach": true, "In Diligence": true,
    comps: true,
  });
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const mapLayersRef = useRef({}); // { stageName: L.layerGroup, comps: L.layerGroup }

  const today = new Date();
  const dateStr = today.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });

  // A v1 snapshot predates the deck owning this payload — it stored only
  // aggregate totals, so the deal tables can't be rebuilt from it. Detected
  // here so the export can say so instead of rendering empty slides.
  const snapshotTooOld = !!snapshot && (snapshot.v || 1) < SNAPSHOT_VERSION;

  useEffect(() => {
    // Rendering a stored week: hydrate straight from the snapshot and skip
    // every query, so the PDF shows what was true that week rather than what
    // is true today. This is the whole point of storing it.
    if (snapshot && !snapshotTooOld) {
      setDeals(snapshot.deals || []);
      setComps(snapshot.comps || []);
      setOutreachCount(snapshot.outreachCount || 0);
      setLoiByDeal(snapshot.loiByDeal || {});
      setAgendaItems(snapshot.agendaItems || []);
      setRecentActivity(snapshot.recentActivity || []);
      setCompletedTasks(snapshot.completedTasks || []);
      setLoading(false);
      return;
    }
    if (snapshotTooOld) { setLoading(false); return; }

    async function load() {
      const PAGE = 1000;
      // Weekly window — Wed 12am EST → Tue 11:59pm EST. SAME window the
      // home dashboard uses; every data source on every slide must scope
      // to this window so the presentation never drifts from the dashboard.
      const wsISO = getWeekStartISO();
      const weISO = getWeekEndISO();
      // Deals
      let allDeals = [], from = 0;
      while (true) {
        const { data } = await supabase.from("deals").select("*").eq("deal_flow", "pipeline").range(from, from + PAGE - 1);
        if (!data?.length) break; allDeals = allDeals.concat(data);
        if (data.length < PAGE) break; from += PAGE;
      }
      setDeals(allDeals.map(dbToDeal));
      // Comps (for map)
      let allComps = []; from = 0;
      while (true) {
        const { data } = await supabase.from("comps").select("id,type,address,city,state,market,latitude,longitude,tenant,buyer,sale_price,lease_rate,building_sf,suite_sf,comp_date").range(from, from + PAGE - 1);
        if (!data?.length) break; allComps = allComps.concat(data);
        if (data.length < PAGE) break; from += PAGE;
      }
      setComps(allComps.filter(c => c.latitude && c.longitude));
      // Outreach count — scoped to the current weekly meeting window.
      let oc = 0; from = 0;
      while (true) {
        const { data } = await supabase.from("outreach_log").select("id").gte("sent_at", wsISO).lte("sent_at", weISO).range(from, from + 999);
        if (!data?.length) break; oc += data.length;
        if (data.length < 1000) break; from += 1000;
      }
      setOutreachCount(oc);
      // Latest LOI offer per deal (for the LOI Submitted slide). Pulls every
      // outreach row that has a deal_id + loi_psf, then keeps only the most
      // recent per deal_id.
      let allOutreach = []; from = 0;
      while (true) {
        const { data } = await supabase.from("outreach_log").select("deal_id, loi_psf, loi_total, sent_at").not("deal_id", "is", null).not("loi_psf", "is", null).order("sent_at", { ascending: false }).range(from, from + 999);
        if (!data?.length) break; allOutreach = allOutreach.concat(data);
        if (data.length < 1000) break; from += 1000;
      }
      const latestByDeal = {};
      for (const row of allOutreach) {
        if (!latestByDeal[row.deal_id]) latestByDeal[row.deal_id] = row;
      }
      setLoiByDeal(latestByDeal);
      // Discussion items
      const { data: items } = await supabase.from("discussion_items").select("*").in("status", ["open", "deferred"]).order("sort_order", { ascending: true });
      setDiscussionItems(items || []);
      // Agenda notes — scope to the current weekly window so notes from
      // prior weeks (that may not have had used_in_presentation flipped)
      // don't leak into this week's live presentation.
      const { data: notes } = await supabase.from("agenda_notes").select("*").gte("created_at", wsISO).lte("created_at", weISO).order("created_at", { ascending: true });
      setAgendaItems((notes || []).filter(n => n.polished || n.text));
      // Recent activity — same Wed-Tue window the dashboard uses, so the
      // weekly stage-entry counts on the summary tile match what's shown
      // on the home page. Limit raised so weeks with many stage changes
      // don't undercount.
      const { data: acts } = await supabase.from("activity_log").select("*").gte("created_at", wsISO).lte("created_at", weISO).order("created_at", { ascending: false }).limit(500);
      setRecentActivity(acts || []);
      // Completed tasks — scoped to the same weekly window. Only tasks
      // that actually hit their target count are surfaced (so manually
      // marked-complete tasks don't pad the number).
      const { data: doneTasks } = await supabase.from("tasks").select("*").eq("status", "completed").gte("updated_at", wsISO).lte("updated_at", weISO).order("updated_at", { ascending: false });
      setCompletedTasks((doneTasks || []).filter(t =>
        !t.target_count || t.current_count >= t.target_count
      ));
      setLoading(false);

      // Exporting the CURRENT week: store exactly what these slides were
      // built from, so re-opening this week later reproduces this PDF. The
      // deck saves it rather than the caller because the deck is what loaded
      // it — anything assembled elsewhere could drift from what's rendered.
      if (exportMode) {
        const { weekLabel, weekStartDate } = getCurrentWeekWindow();
        const payload = {
          v: SNAPSHOT_VERSION,
          savedAt: new Date().toISOString(),
          weekLabel,
          weekStart: weekStartDate,
          deals: allDeals.map(dbToDeal).map(trimDeal),
          loiByDeal: latestByDeal,
          outreachCount: oc,
          agendaItems: (notes || []).filter(n => n.polished || n.text),
          recentActivity: acts || [],
          completedTasks: (doneTasks || []).filter(t =>
            !t.target_count || t.current_count >= t.target_count
          ),
          // Comps are map-only and the map is excluded from the PDF, so
          // they're deliberately not stored — they'd triple the payload.
        };
        try {
          const { data: existing } = await supabase
            .from("presentation_history").select("id").eq("week_start", weekStartDate).limit(1);
          if (existing?.length) {
            await supabase.from("presentation_history").update({
              week_label: weekLabel,
              presented_at: new Date().toISOString(),
              presented_by: presentedBy,
              presented_by_name: presentedByName,
              snapshot: payload,
            }).eq("id", existing[0].id);
          } else {
            await supabase.from("presentation_history").insert({
              week_label: weekLabel,
              week_start: weekStartDate,
              presented_by: presentedBy,
              presented_by_name: presentedByName,
              snapshot: payload,
            });
          }
          onSnapshotSaved?.();
        } catch (e) {
          console.warn("Failed to save weekly snapshot:", e);
        }
      }
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // In export mode, open the print dialog by itself once the deal data has
  // landed and the browser has had a frame to lay the pages out — so the
  // button is one click, not click-then-print. Guarded by a ref so a
  // re-render can't reopen the dialog behind the first one. If the user
  // dismisses it, the Save as PDF button in the toolbar is still there.
  const autoPrintedRef = useRef(false);
  useEffect(() => {
    if (!exportMode || loading || snapshotTooOld || autoPrintedRef.current) return;
    autoPrintedRef.current = true;
    const t = setTimeout(() => window.print(), 600);
    return () => clearTimeout(t);
  }, [exportMode, loading, snapshotTooOld]);

  // Keyboard nav
  const totalSlides = 6; // Title, Weekly Summary, LOI, UW, Tracking, Map
  const handleKey = useCallback((e) => {
    if (e.key === "Escape") onClose?.();
    if (e.key === "ArrowRight" || e.key === " " || e.key === "Enter") setSlide(s => Math.min(s + 1, totalSlides - 1));
    if (e.key === "ArrowLeft" || e.key === "Backspace") setSlide(s => Math.max(s - 1, 0));
  }, [onClose]);
  useEffect(() => { document.addEventListener("keydown", handleKey); return () => document.removeEventListener("keydown", handleKey); }, [handleKey]);

  // Init Leaflet map when we land on the map slide
  useEffect(() => {
    if (slide !== 5 || !mapRef.current) return;
    // If the map already exists but the container was detached (React
    // unmounted the slide's DOM), destroy and recreate.
    if (mapInstanceRef.current) {
      try { mapInstanceRef.current.remove(); } catch {}
      mapInstanceRef.current = null;
      mapLayersRef.current = {};
    }
    const L = window.L;
    if (!L) return;
    const map = L.map(mapRef.current, { center: [28.5, -82], zoom: 6, zoomControl: true, attributionControl: false });
    L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", { maxZoom: 19 }).addTo(map);
    mapInstanceRef.current = map;

    // Build layer groups per stage + comps
    const L2 = L; // alias for clarity
    const stageGroups = {};
    const stageColors = { "LOI Submitted": "#e93529", "Unsolicited Offer": "#c8922f", "Underwriting": "#b4b4b4", "Screening": "#8a8a8a", "Tracking": "#8a8a8a", "Sourcing": "#8a8a8a", "Outreach": "#8a8a8a", "In Diligence": "#4f9d76" };
    Object.keys(stageColors).forEach(s => { stageGroups[s] = L2.layerGroup().addTo(map); });
    const compGroup = L2.layerGroup().addTo(map);

    const activeDeals = deals.filter(d => !["Passed", "Lost", "Dead"].includes(d.stage));
    activeDeals.forEach(d => {
      const lat = d.latitude;
      const lng = d.longitude;
      if (!lat || !lng) return;
      const name = dealName(d) || "Deal";
      const stageColors = { "LOI Submitted": "#e93529", "Unsolicited Offer": "#c8922f", "Underwriting": "#b4b4b4", "Screening": "#8a8a8a", "Tracking": "#8a8a8a", "Sourcing": "#8a8a8a", "Outreach": "#8a8a8a", "In Diligence": "#4f9d76" };
      const color = stageColors[d.stage] || "#6a6a6a";
      const icon = L.divIcon({
        html: `<div style="background:${color};color:#fff;border-radius:50% 50% 50% 0;width:20px;height:20px;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;border:2px solid rgba(255,255,255,0.7);box-shadow:0 2px 6px rgba(0,0,0,0.4);font-size:7px;font-weight:900;font-family:Montserrat"><span style="transform:rotate(45deg)">${d.stage?.[0] || "?"}</span></div>`,
        className: "", iconSize: [20, 20], iconAnchor: [10, 20],
      });
      // Rich tooltip matching the Map page style
      const addr = d.addresses?.[0] || "";
      const ask = d.askingPrice ? fmtMoney(d.askingPrice) : "TBD";
      const sf = d.squareFeet ? fmtSF(d.squareFeet) : "";
      const psf = d.squareFeet && d.askingPrice ? fmtPSF(Math.round(d.askingPrice / d.squareFeet)) : "";
      const uw = d.underwrittenPrice ? `UW: ${fmtMoney(d.underwrittenPrice)}` : "";
      const tipHtml = `<div style="font-family:Montserrat,sans-serif;line-height:1.5;min-width:180px">
        <strong style="font-size:12px;display:block;margin-bottom:2px">${name}</strong>
        ${name !== addr ? `<span style="font-size:10px;color:#666;display:block">${addr}</span>` : ""}
        <span style="font-size:10px;color:${color};font-weight:700">${d.stage}</span>
        ${d.market ? ` · <span style="font-size:10px;color:#888">${d.market}</span>` : ""}
        ${sf ? `<br><span style="font-size:10px;color:#444">${sf}</span>` : ""}
        ${ask !== "TBD" ? ` · <span style="font-size:10px;color:#444">${ask}</span>` : ""}
        ${psf ? ` · <span style="font-size:10px;color:#444">${psf}/SF</span>` : ""}
        ${uw ? `<br><span style="font-size:10px;color:#e93529;font-weight:600">${uw}</span>` : ""}
      </div>`;
      const marker = L.marker([lat, lng], { icon }).bindTooltip(tipHtml, { sticky: true, className: "pp-tip" });
      const group = stageGroups[d.stage];
      if (group) group.addLayer(marker); else marker.addTo(map);
    });

    // Add comp pins (smaller dots)
    comps.forEach(c => {
      const color = c.type === "sale" ? "#c8922f" : "#b4b4b4";
      const icon = L.divIcon({
        html: `<div style="background:${color};width:8px;height:8px;border-radius:50%;border:1px solid rgba(255,255,255,0.6);box-shadow:0 1px 3px rgba(0,0,0,0.3)"></div>`,
        className: "", iconSize: [8, 8], iconAnchor: [4, 4],
      });
      const compLabel = c.type === "sale" ? "Sale Comp" : "Lease Comp";
      const compDetail = c.type === "sale"
        ? `${c.buyer ? `Buyer: ${c.buyer}` : ""}${c.sale_price ? ` · ${fmtMoney(c.sale_price)}` : ""}${c.building_sf ? ` · ${fmtSF(c.building_sf)}` : ""}`
        : `${c.tenant || ""}${c.lease_rate ? ` · $${Number(c.lease_rate).toFixed(2)}/SF` : ""}${c.suite_sf ? ` · ${fmtSF(c.suite_sf)}` : c.building_sf ? ` · ${fmtSF(c.building_sf)}` : ""}`;
      const compDate = c.comp_date ? ` · ${new Date(c.comp_date).toLocaleDateString("en-US", { month: "short", year: "numeric" })}` : "";
      const compTip = `<div style="font-family:Montserrat,sans-serif;line-height:1.5;min-width:160px">
        <strong style="font-size:11px;display:block;margin-bottom:2px">${c.address || "Unknown"}</strong>
        <span style="font-size:10px;color:${color};font-weight:700">${compLabel}</span>${compDate}
        ${c.market ? `<br><span style="font-size:9px;color:#888">${c.market}</span>` : ""}
        ${compDetail ? `<br><span style="font-size:10px;color:#444">${compDetail}</span>` : ""}
      </div>`;
      compGroup.addLayer(L.marker([c.latitude, c.longitude], { icon }).bindTooltip(compTip, { sticky: true, className: "pp-tip" }));
    });

    // Store refs for filter toggling
    mapLayersRef.current = { ...stageGroups, comps: compGroup };

    setTimeout(() => map.invalidateSize(), 100);
    return () => { /* keep map alive for re-visits to this slide */ };
  }, [slide, deals, comps]);

  // Toggle map layer visibility when filters change
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    Object.entries(mapLayersRef.current).forEach(([key, group]) => {
      if (mapFilters[key]) { if (!map.hasLayer(group)) map.addLayer(group); }
      else { if (map.hasLayer(group)) map.removeLayer(group); }
    });
  }, [mapFilters]);

  // Fix map glitch: when navigating away and back, the map container
  // gets detached from the DOM. Re-attach it on every visit to slide 6
  // and force a size recalculation so tiles render.
  useEffect(() => {
    if (slide !== 5) return;
    const map = mapInstanceRef.current;
    if (map && mapRef.current) {
      // Re-attach the map container to the current DOM element if needed
      if (map.getContainer() !== mapRef.current) {
        // Map container was orphaned — need to rebuild
        map.remove();
        mapInstanceRef.current = null;
      } else {
        // Container still in DOM — just resize
        setTimeout(() => map.invalidateSize(), 100);
      }
    }
  }, [slide]);

  if (loading) {
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: BG, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONT }}>
        <div className="flex gap-1.5">
          <span className="w-2 h-2 bg-pp-red rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
          <span className="w-2 h-2 bg-pp-red rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
          <span className="w-2 h-2 bg-pp-red rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
        </div>
      </div>
    );
  }

  // Build per-stage sets of deal IDs that entered each stage during the
  // current weekly meeting window — sourced from activity_log via
  // recentActivity. Every per-stage detail slide and the Outreach by
  // Market block uses these sets so the entire deck represents *this
  // week's activity*, never cumulative pipeline state.
  const idsByStage = {};
  const weeklyEntered = {};
  for (const a of recentActivity || []) {
    let target = null;
    if (a.action === "stage_changed") target = a.metadata?.new_stage;
    else if (a.action === "deal_created") target = a.metadata?.stage;
    if (!target) continue;
    weeklyEntered[target] = (weeklyEntered[target] || 0) + 1;
    if (a.entity_id) {
      if (!idsByStage[target]) idsByStage[target] = new Set();
      idsByStage[target].add(a.entity_id);
    }
  }
  // Sourcing is the Sourcing-fired LOI stage (formerly "Outreach", before
  // that "Unsolicited Offer"), so it counts toward LOIs sent as those did.
  const loiSentWk = (weeklyEntered["LOI Submitted"] || 0)
    + (weeklyEntered["Sourcing"] || 0)
    + (weeklyEntered["Outreach"] || 0)
    + (weeklyEntered["Unsolicited Offer"] || 0);
  const uwStartedWk = weeklyEntered["Underwriting"] || 0;
  const trackingStartedWk = weeklyEntered["Tracking"] || 0;

  const loiIds = new Set([
    ...(idsByStage["LOI Submitted"] || []),
    ...(idsByStage["Sourcing"] || []),
    ...(idsByStage["Outreach"] || []),
    ...(idsByStage["Unsolicited Offer"] || []),
  ]);
  const uwIds = idsByStage["Underwriting"] || new Set();
  const trackingIds = idsByStage["Tracking"] || new Set();
  const outreachIds = new Set([
    ...(idsByStage["Sourcing"] || []),
    ...(idsByStage["Outreach"] || []),
    ...(idsByStage["Unsolicited Offer"] || []),
  ]);

  const loiDeals = deals.filter(d => loiIds.has(d.id));
  const uwDeals = deals.filter(d => uwIds.has(d.id));
  const trackingDeals = deals.filter(d => trackingIds.has(d.id));
  const outreachDealsWk = deals.filter(d => outreachIds.has(d.id));

  // For the LOI slide we want $/SF and totals to reflect the actual LOI we
  // sent (loi_psf × sf), not the seller's ask. Helper functions here switch
  // source depending on which slide is rendering.
  const loiPsfOf = (d) => loiByDeal[d.id]?.loi_psf || null;
  const effectivePsf = (d, useLoi) => {
    if (useLoi) {
      const lp = loiPsfOf(d);
      if (lp) return lp;
    }
    if (d.squareFeet && d.askingPrice) return Math.round(d.askingPrice / d.squareFeet);
    return null;
  };
  const effectiveTotal = (d, useLoi) => {
    if (useLoi) {
      const row = loiByDeal[d.id];
      if (row?.loi_total) return row.loi_total;
      if (row?.loi_psf && d.squareFeet) return Math.round(row.loi_psf * d.squareFeet);
    }
    return d.askingPrice || null;
  };

  const totalSF = (arr) => arr.reduce((s, d) => s + (d.squareFeet || 0), 0);
  const totalValue = (arr, useLoi = false) => arr.reduce((s, d) => s + (effectiveTotal(d, useLoi) || 0), 0);
  // SF-weighted average $/SF over rows that actually have a PSF available.
  const avgPSF = (arr, useLoi = false) => {
    let dollars = 0, sf = 0;
    for (const d of arr) {
      const psf = effectivePsf(d, useLoi);
      const s = d.squareFeet || 0;
      if (psf && s) { dollars += psf * s; sf += s; }
    }
    return sf > 0 ? Math.round(dollars / sf) : 0;
  };

  // ── Shared components ──
  const SlideHeader = ({ title }) => (
    <div style={{ background: CARD, borderBottom: `1px solid ${BORDER}`, padding: "12px 40px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <PPLogo height={20} variant="red" />
      <span style={{ color: MUTED, fontFamily: FONT, fontSize: 12, fontWeight: 600 }}>{title}</span>
      <span style={{ color: MUTED, fontFamily: FONT, fontSize: 12 }}>
        {today.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
      </span>
    </div>
  );

  const StatsBar = ({ stats }) => (
    <div style={{ display: "flex", borderTop: `3px solid ${PP_RED}`, background: CARD, marginTop: "auto" }}>
      {stats.map((s, i) => (
        <div key={i} style={{ flex: 1, textAlign: "center", padding: "16px 0", borderRight: i < stats.length - 1 ? `1px solid ${BORDER}` : "none" }}>
          <div style={{ fontFamily: FONT, fontSize: 24, fontWeight: 900, color: PP_RED }}>{s.value}</div>
          <div style={{ fontFamily: FONT, fontSize: 8, fontWeight: 700, textTransform: "uppercase", letterSpacing: 2, color: MUTED, marginTop: 4 }}>{s.label}</div>
        </div>
      ))}
    </div>
  );

  const DealTable = ({ items, columns, useLoi = false }) => (
    <div style={{ margin: "0 40px", flex: 1, overflowY: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONT }}>
        <thead>
          <tr>{columns.map(c => (
            <th key={c.key} style={{ textAlign: "left", padding: "8px 10px", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: MUTED, borderBottom: `2px solid ${PP_RED}`, background: CARD }}>{c.label}</th>
          ))}</tr>
        </thead>
        <tbody>
          {items.map((deal, i) => {
            const name = dealName(deal) || "—";
            const addr = deal.addresses?.[0] || "";
            const sf = deal.squareFeet;
            const psf = effectivePsf(deal, useLoi);
            const total = effectiveTotal(deal, useLoi);
            const notes = (deal.sourceNotes || "").replace(/^Name:.*\n\n?/m, "").split("\n")[0]?.slice(0, 100) || "";
            const vals = {
              property: (<div><div style={{ fontWeight: 700, color: TEXT, fontSize: 12 }}>{name}</div>{name !== addr && <div style={{ fontSize: 10, color: MUTED, marginTop: 1 }}>{addr}</div>}</div>),
              market: deal.market || "—", sf: fmtSF(sf), askPsf: fmtPSF(psf), total: fmtMoney(total),
              notes: <span style={{ fontSize: 10, color: MUTED, fontStyle: "italic" }}>{notes}</span>,
            };
            return (
              <tr key={deal.id} style={{ borderBottom: `1px solid #e4e4e4`, background: i % 2 === 0 ? CARD : "#fafafa" }}>
                {columns.map(c => (<td key={c.key} style={{ padding: "10px 10px", fontSize: 11, color: TEXT, verticalAlign: "top" }}>{vals[c.key] ?? "—"}</td>))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const tableColumns = [
    { key: "property", label: "Property" }, { key: "market", label: "Market" },
    { key: "sf", label: "SF" }, { key: "askPsf", label: "$/SF" },
    { key: "total", label: "Total" }, { key: "notes", label: "Notes" },
  ];

  // ── Slides ──
  const renderSlide = (idx = slide) => {
    switch (idx) {
      // Title
      case 0: return (
        <div style={{ display: "flex", flexDirection: "column", height: "100%", background: BG }}>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", padding: 60 }}>
            <PPLogo height={48} variant="red" />
            <div style={{ marginTop: 32, borderLeft: `4px solid ${PP_RED}`, paddingLeft: 24 }}>
              <div style={{ fontFamily: FONT, fontSize: 36, fontWeight: 800, color: TEXT }}>Pipeline & Acquisition Review</div>
              <div style={{ width: 80, height: 3, background: PP_RED, marginTop: 16 }} />
              <div style={{ fontFamily: FONT, fontSize: 14, color: MUTED, marginTop: 16 }}>{dateStr} &middot; Weekly Executive Call Package</div>
              <div style={{ fontFamily: FONT, fontSize: 10, color: PP_RED, marginTop: 8, letterSpacing: 3, textTransform: "uppercase", fontWeight: 700 }}>
                Confidential &middot; Internal Distribution Only
              </div>
            </div>
          </div>
          <div style={{ display: "flex", borderTop: `3px solid ${PP_RED}`, background: CARD }}>
            {[
              { value: loiSentWk, label: "LOIs Sent (Wk)", accent: true },
              { value: uwStartedWk, label: "New UW (Wk)", accent: true },
              { value: trackingStartedWk, label: "New Tracking (Wk)", accent: false },
              { value: outreachCount, label: "Outreach (Wk)", accent: true },
            ].map((s, i) => (
              <div key={i} style={{ flex: 1, textAlign: "center", padding: "20px 0", borderRight: i < 3 ? `1px solid ${BORDER}` : "none" }}>
                <div style={{ fontFamily: FONT, fontSize: 36, fontWeight: 900, color: s.accent ? PP_RED : TEXT }}>{s.value}</div>
                <div style={{ fontFamily: FONT, fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 2, color: MUTED, marginTop: 4 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      );

      // Weekly Summary — executive dashboard: outreach stats + agenda + accomplishments + goals
      case 1: {
        const ACTION_LABELS = { loi_sent: "LOI Sent", deal_created: "Deal Created", stage_changed: "Stage Changed", comp_added: "Comp Added", property_added: "Property Added", property_deleted: "Property Deleted", contact_added: "Contact Added" };
        const actSummary = {};
        recentActivity.forEach(a => {
          const key = a.action;
          if (!actSummary[key]) actSummary[key] = { label: ACTION_LABELS[key] || key, count: 0, examples: [] };
          actSummary[key].count++;
          if (actSummary[key].examples.length < 2) actSummary[key].examples.push(a.description);
        });
        const actBullets = Object.values(actSummary).sort((a, b) => b.count - a.count);

        // Outreach market breakdown — only deals that entered Outreach /
        // Unsolicited Offer THIS WEEK (sourced from outreachDealsWk above).
        // If no outreach happened this week, the section renders empty.
        const markets = {};
        outreachDealsWk.forEach(d => {
          const m = d.market || "Other";
          if (!markets[m]) markets[m] = { count: 0, sf: 0 };
          markets[m].count++; markets[m].sf += d.squareFeet || 0;
        });
        // TEMP one-time data scrub (added 2026-05-05): hide stray entries
        // that surfaced for the week of May 5. Remove this skip-list once
        // the underlying activity_log rows are cleaned up.
        const SKIP_MARKETS = new Set([
          "West Houston",
          "DFW - West DFW Airport, Stemmons/Valwood, E DFW Air/Las Colinas",
        ]);
        const marketList = Object.entries(markets)
          .filter(([m]) => !SKIP_MARKETS.has(m))
          .sort((a, b) => b[1].count - a[1].count)
          .slice(0, 4);

        return (
          <div style={{ display: "flex", flexDirection: "column", height: "100%", background: BG }}>
            <SlideHeader title="Weekly Summary" />
            <div style={{ flex: 1, overflowY: "auto", padding: "16px 40px" }}>
              {/* ── Row 1: KPI strip — sourced identically to the home dashboard ── */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
                {[
                  { value: loiSentWk, label: "LOIs Sent (Wk)", color: PP_RED },
                  { value: uwStartedWk, label: "New UW (Wk)", color: PP_RED },
                  { value: trackingStartedWk, label: "New Tracking (Wk)", color: "#4f9d76" },
                  { value: outreachCount, label: "Outreach (Wk)", color: "#8a8a8a" },
                ].map((s, i) => (
                  <div key={i} style={{ background: CARD, borderRadius: 10, border: `1px solid ${BORDER}`, padding: "14px 16px", textAlign: "center", borderBottom: `3px solid ${s.color}` }}>
                    <div style={{ fontFamily: FONT, fontSize: 28, fontWeight: 900, color: s.color }}>{s.value}</div>
                    <div style={{ fontFamily: FONT, fontSize: 8, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.5, color: MUTED, marginTop: 4 }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* ── Row 2: 3-column content cards ── */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
                {/* Agenda Items */}
                <div style={{ background: CARD, borderRadius: 10, border: `1px solid ${BORDER}`, padding: "14px 16px", borderLeft: `4px solid ${PP_RED}` }}>
                  <h3 style={{ fontFamily: FONT, fontSize: 11, fontWeight: 800, color: PP_RED, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Agenda Items</h3>
                  {agendaItems.length === 0 ? (
                    <p style={{ fontFamily: FONT, fontSize: 11, color: MUTED }}>No items — add on Home tab.</p>
                  ) : (
                    <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                      {agendaItems.slice(0, 6).map((item, i) => (
                        <li key={item.id || i} style={{ fontFamily: FONT, fontSize: 11, color: TEXT, padding: "5px 0", borderBottom: i < Math.min(agendaItems.length, 6) - 1 ? `1px solid #f4f4f4` : "none", lineHeight: 1.4 }}>
                          <span style={{ color: PP_RED, fontWeight: 700, marginRight: 4 }}>{i + 1}.</span>
                          {item.polished || item.text}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Things We Got Done */}
                <div style={{ background: CARD, borderRadius: 10, border: `1px solid ${BORDER}`, padding: "14px 16px", borderLeft: `4px solid #4f9d76` }}>
                  <h3 style={{ fontFamily: FONT, fontSize: 11, fontWeight: 800, color: "#4f9d76", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>What We Got Done</h3>
                  {actBullets.length === 0 ? (
                    <p style={{ fontFamily: FONT, fontSize: 11, color: MUTED }}>No activity this week.</p>
                  ) : (
                    <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                      {actBullets.slice(0, 5).map((b, i) => (
                        <li key={i} style={{ fontFamily: FONT, fontSize: 11, color: TEXT, padding: "5px 0", borderBottom: i < Math.min(actBullets.length, 5) - 1 ? `1px solid #f4f4f4` : "none" }}>
                          <span style={{ fontWeight: 700 }}>{b.count}× </span>{b.label}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Goals */}
                <div style={{ background: CARD, borderRadius: 10, border: `1px solid ${BORDER}`, padding: "14px 16px", borderLeft: `4px solid #8a8a8a` }}>
                  <h3 style={{ fontFamily: FONT, fontSize: 11, fontWeight: 800, color: "#8a8a8a", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Goals</h3>
                  {completedTasks.length === 0 ? (
                    <p style={{ fontFamily: FONT, fontSize: 11, color: MUTED }}>No goals completed.</p>
                  ) : (
                    <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                      {completedTasks.slice(0, 5).map((t, i) => (
                        <li key={t.id} style={{ fontFamily: FONT, fontSize: 11, color: TEXT, padding: "5px 0", borderBottom: i < Math.min(completedTasks.length, 5) - 1 ? `1px solid #f4f4f4` : "none" }}>
                          <span style={{ color: "#4f9d76", marginRight: 4 }}>✓</span>
                          {t.title}
                          {t.target_count ? <span style={{ color: MUTED, fontSize: 10 }}> ({t.current_count}/{t.target_count})</span> : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* ── Row 3: Outreach market cards (compact strip) ── */}
              {marketList.length > 0 && (
                <div>
                  <h3 style={{ fontFamily: FONT, fontSize: 11, fontWeight: 800, color: MUTED, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Outreach by Market</h3>
                  <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(marketList.length, 4)}, 1fr)`, gap: 10 }}>
                    {marketList.map(([market, data]) => (
                      <div key={market} style={{ background: CARD, borderRadius: 8, border: `1px solid ${BORDER}`, padding: "10px 14px", borderLeft: `3px solid ${PP_RED}` }}>
                        <div style={{ fontFamily: FONT, fontSize: 12, fontWeight: 800, color: TEXT }}>{market}</div>
                        <div style={{ fontFamily: FONT, fontSize: 10, color: PP_RED, fontWeight: 700, marginTop: 2 }}>
                          {data.count} props &middot; {data.sf > 1e6 ? `${(data.sf / 1e6).toFixed(1)}M SF` : fmtSF(data.sf)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      }

      // LOIs Sent This Week — deals that entered LOI Submitted / Unsolicited
      // Offer during the current weekly window. Empty when no LOIs went out.
      case 2: return (
        <div style={{ display: "flex", flexDirection: "column", height: "100%", background: BG }}>
          <SlideHeader title="LOIs Sent This Week" />
          <div style={{ padding: "20px 40px 8px", fontFamily: FONT }}>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: TEXT }}>LOIs Sent This Week</h2>
            <p style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>{loiDeals.length} this week &middot; {fmtSF(totalSF(loiDeals))} &middot; {fmtMoney(totalValue(loiDeals, true))} offered</p>
          </div>
          <DealTable items={loiDeals} columns={tableColumns} useLoi={true} />
          <StatsBar stats={[{ value: fmtSF(totalSF(loiDeals)), label: "Total SF" }, { value: fmtPSF(avgPSF(loiDeals, true)), label: "Avg LOI $/SF" }, { value: fmtMoney(totalValue(loiDeals, true)), label: "Combined Offer" }]} />
        </div>
      );

      // New Underwriting This Week — deals that entered Underwriting in the
      // current weekly window.
      case 3: return (
        <div style={{ display: "flex", flexDirection: "column", height: "100%", background: BG }}>
          <SlideHeader title="New Underwriting This Week" />
          <div style={{ padding: "20px 40px 8px", fontFamily: FONT }}>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: TEXT }}>New Underwriting This Week</h2>
            <p style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>{uwDeals.length} this week &middot; {fmtSF(totalSF(uwDeals))}</p>
          </div>
          <DealTable items={uwDeals} columns={tableColumns} />
          <StatsBar stats={[{ value: fmtSF(totalSF(uwDeals)), label: "Total SF" }, { value: uwDeals.length, label: "Properties" }]} />
        </div>
      );

      // New Tracking This Week — deals that entered Tracking in the current
      // weekly window.
      case 4: return (
        <div style={{ display: "flex", flexDirection: "column", height: "100%", background: BG }}>
          <SlideHeader title="New Tracking This Week" />
          <div style={{ padding: "20px 40px 8px", fontFamily: FONT }}>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: TEXT }}>New Tracking This Week</h2>
            <p style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>{trackingDeals.length} this week &middot; {fmtSF(totalSF(trackingDeals))} &middot; Monitoring for acquisition opportunity</p>
          </div>
          <DealTable items={trackingDeals} columns={tableColumns} />
          <StatsBar stats={[{ value: fmtSF(totalSF(trackingDeals)), label: "Total SF" }, { value: trackingDeals.length, label: "Properties" }, { value: fmtPSF(avgPSF(trackingDeals)), label: "Avg Ask $/SF" }]} />
        </div>
      );

      // Map (was case 6, now case 5 — Outreach slide removed)
      case 5: return (
        <div style={{ display: "flex", flexDirection: "column", height: "100%", background: BG }}>
          <SlideHeader title="Pipeline & Comp Map" />
          <div style={{ padding: "12px 40px 8px", fontFamily: FONT }}>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: TEXT }}>Pipeline & Comp Map</h2>
            <p style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>
              Active deals (colored pins) + lease/sale comps (small dots) &middot; {comps.length} comps plotted
            </p>
          </div>
          {/* Stage filter pills */}
          <div style={{ padding: "0 40px 8px", display: "flex", flexWrap: "wrap", gap: 6 }}>
            {Object.entries({
              "LOI Submitted": "#e93529", "Unsolicited Offer": "#c8922f", "Underwriting": "#b4b4b4",
              "Screening": "#8a8a8a", "Tracking": "#8a8a8a", "In Diligence": "#4f9d76",
            }).map(([stage, color]) => {
              const on = mapFilters[stage];
              return (
                <button key={stage} onClick={() => setMapFilters(f => ({ ...f, [stage]: !f[stage] }))}
                  style={{
                    display: "flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 20,
                    border: `1px solid ${on ? color : BORDER}`, background: on ? `${color}15` : CARD,
                    cursor: "pointer", fontFamily: FONT, fontSize: 10, fontWeight: 700,
                    color: on ? color : MUTED, transition: "all 0.15s",
                  }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: on ? color : "#ccc" }} />
                  {stage}
                </button>
              );
            })}
            <button onClick={() => setMapFilters(f => ({ ...f, comps: !f.comps }))}
              style={{
                display: "flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 20,
                border: `1px solid ${mapFilters.comps ? "#b4b4b4" : BORDER}`, background: mapFilters.comps ? "#b4b4b415" : CARD,
                cursor: "pointer", fontFamily: FONT, fontSize: 10, fontWeight: 700,
                color: mapFilters.comps ? "#b4b4b4" : MUTED, transition: "all 0.15s",
              }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: mapFilters.comps ? "#b4b4b4" : "#ccc" }} />
              Comps ({comps.length})
            </button>
          </div>
          <div style={{ flex: 1, margin: "0 40px 16px", borderRadius: 12, overflow: "hidden", border: `1px solid ${BORDER}` }}>
            <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
          </div>
        </div>
      );

      default: return null;
    }
  };

  // ── Export mode ───────────────────────────────────────────────────────────
  // Same slides, laid out as a document instead of a projector: every slide
  // stacked one per printed page, no nav, and the browser's print dialog
  // fires once the content has settled. Printing through the print pipeline
  // rather than a canvas rasterizer keeps the tables as selectable text.
  //
  // The map slide is left out. It's a live Leaflet canvas whose tiles load
  // asynchronously and frequently don't make it into a print job at all, so
  // including it would mean shipping a grey rectangle. Getting the map into
  // the PDF properly needs a static map image, which is its own change.
  if (exportMode) {
    const pages = [0, 1, 2, 3, 4];   // title, weekly summary, LOI, UW, tracking
    return (
      <div
        style={{ position: "absolute", inset: 0, zIndex: 9999, background: "#ffffff", fontFamily: FONT, overflowY: "auto" }}
      >
        {/* Screen-only controls. Stripped from the print output. */}
        <div
          data-print-hide
          style={{
            position: "sticky", top: 0, zIndex: 2, background: CARD,
            borderBottom: `1px solid ${BORDER}`, padding: "10px 24px",
            display: "flex", alignItems: "center", gap: 12,
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 700, color: TEXT }}>
            {snapshot ? `Weekly PDF · ${snapshot.weekLabel || "saved week"}` : "Weekly PDF"}
          </span>
          <span style={{ fontSize: 11, color: MUTED }}>
            {snapshotTooOld
              ? "This week was saved before Proxy stored full deal data — it can't be rebuilt."
              : snapshot
                ? `As saved ${snapshot.savedAt ? new Date(snapshot.savedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""} · ${pages.length} pages · map slide not included`
                : `${pages.length} pages · landscape · the interactive map slide isn't included`}
          </span>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            {!snapshotTooOld && (
              <button
                onClick={() => window.print()}
                style={{
                  background: PP_RED, color: "#ffffff", border: "none", borderRadius: 6,
                  padding: "8px 14px", fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer",
                }}
              >
                Save as PDF
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                background: CARD, color: TEXT, border: `1px solid ${BORDER}`, borderRadius: 6,
                padding: "8px 14px", fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer",
              }}
            >
              Close
            </button>
          </div>
        </div>

        {snapshotTooOld ? (
          <div data-print-hide style={{ padding: 48, maxWidth: 620, margin: "0 auto", fontFamily: FONT }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: TEXT, marginBottom: 10 }}>
              Nothing to rebuild for this week
            </div>
            <p style={{ fontSize: 13, color: MUTED, lineHeight: 1.6, margin: 0 }}>
              Weeks presented before this change stored only headline totals — deal
              counts, square footage, outreach — not the deal records the slides are
              built from. There's no way to reconstruct the tables from what was kept,
              and filling them with today's deals would label current data as that
              week's, which is worse than showing nothing.
            </p>
            <p style={{ fontSize: 13, color: MUTED, lineHeight: 1.6, marginTop: 12 }}>
              Every week exported from now on stores the full record and will rebuild
              exactly as it looked.
            </p>
          </div>
        ) : (
        /* One page per slide, sized to landscape letter at 96dpi. */
        <div style={{ padding: 24, display: "flex", flexDirection: "column", alignItems: "center", gap: 24 }}>
          {pages.map(i => (
            <div
              key={i}
              data-print-page
              style={{
                width: 1056, height: 790, background: BG, overflow: "hidden",
                border: `1px solid ${BORDER}`, flexShrink: 0,
              }}
            >
              {renderSlide(i)}
            </div>
          ))}
        </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: BG, fontFamily: FONT, display: "flex", flexDirection: "column" }}>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {renderSlide()}
      </div>
      {/* Navigation bar */}
      <div style={{ height: 44, background: CARD, borderTop: `1px solid ${BORDER}`, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 24px" }}>
        <button onClick={onClose} style={{ background: "none", border: "none", color: MUTED, cursor: "pointer", fontFamily: FONT, fontSize: 12, fontWeight: 600 }}>✕ Exit</button>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <button onClick={() => setSlide(s => Math.max(s - 1, 0))} disabled={slide === 0} style={{ background: "none", border: "none", color: slide === 0 ? "#ccc" : TEXT, cursor: slide === 0 ? "default" : "pointer", fontSize: 18, padding: "4px 8px" }}>‹</button>
          <span style={{ color: MUTED, fontSize: 11, fontFamily: FONT, fontWeight: 600, minWidth: 60, textAlign: "center" }}>{slide + 1} / {totalSlides}</span>
          <button onClick={() => setSlide(s => Math.min(s + 1, totalSlides - 1))} disabled={slide === totalSlides - 1} style={{ background: "none", border: "none", color: slide === totalSlides - 1 ? "#ccc" : TEXT, cursor: slide === totalSlides - 1 ? "default" : "pointer", fontSize: 18, padding: "4px 8px" }}>›</button>
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {Array.from({ length: totalSlides }).map((_, i) => (
            <button key={i} onClick={() => setSlide(i)} style={{ width: 8, height: 8, borderRadius: "50%", border: "none", cursor: "pointer", background: i === slide ? PP_RED : "#ccc" }} />
          ))}
        </div>
      </div>
    </div>
  );
}
